/**
 * "Fill it from today's position, or from after the plan?" — asked on every pre-filled Services
 * Australia form (owner, 3 Oct 2026).
 *
 * Both are right at different times. Today's position is what to lodge now, including the
 * time-critical case of moving into care straight away and restructuring afterwards. The position
 * after a plan is what to lodge once the changes are made, and never before: the reminder says so
 * beside the choice and the file is named "after-plan".
 */
import React, { useEffect, useState } from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { planLabel, positionAfterPlan, type PlanFacts, type SavedPlan } from '../../features/forms/planPosition.ts';

export type PositionChoice = { mode: 'today' } | { mode: 'plan'; planId: string };

export interface ResolvedPosition {
    status: 'ready' | 'loading' | 'failed';
    factFind: DraftFactFind;
    afterPlan: boolean;
    plan: SavedPlan | null;
    caveats: string[];
    /** What the plan does that the profile cannot record (a let home); empty for today's position. */
    facts: PlanFacts;
    reason?: string;
}

/** The profile the chosen position stands for, rebuilt whenever the choice changes. */
export function useResolvedPosition(factFind: DraftFactFind, plans: SavedPlan[], choice: PositionChoice): ResolvedPosition {
    const plan = choice.mode === 'plan' ? plans.find((p) => p.id === choice.planId) || null : null;
    const [state, setState] = useState<ResolvedPosition>({ status: 'ready', factFind, afterPlan: false, plan: null, caveats: [], facts: {} });
    useEffect(() => {
        if (!plan) {
            setState({ status: 'ready', factFind, afterPlan: false, plan: null, caveats: [], facts: {} });
            return undefined;
        }
        let cancelled = false;
        setState({ status: 'loading', factFind, afterPlan: true, plan, caveats: [], facts: {} });
        positionAfterPlan(factFind, plan).then((r) => {
            if (cancelled) return;
            setState(r.ok
                ? { status: 'ready', factFind: r.factFind, afterPlan: true, plan, caveats: r.caveats, facts: r.facts }
                : { status: 'failed', factFind, afterPlan: true, plan, caveats: [], facts: {}, reason: r.reason });
        });
        return () => { cancelled = true; };
    }, [factFind, plan]);
    return state;
}

interface PositionPickerProps {
    plans: SavedPlan[];
    choice: PositionChoice;
    onChange: (c: PositionChoice) => void;
    resolved: ResolvedPosition;
}

export function PositionPicker({ plans, choice, onChange, resolved }: PositionPickerProps) {
    const selectedPlan = choice.mode === 'plan' ? plans.find((p) => p.id === choice.planId) : null;
    const tab = (on: boolean) => `rounded-lg border-2 px-3 py-2 text-left text-sm font-semibold ${on ? 'border-blue-600 bg-blue-50 text-blue-900' : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300'}`;
    return (
        <div>
            <p className="text-sm font-semibold text-gray-800 mb-2">Fill it in from</p>
            <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Fill the form in from">
                <button type="button" role="radio" aria-checked={choice.mode === 'today'} onClick={() => onChange({ mode: 'today' })} className={tab(choice.mode === 'today')}>
                    Your position today
                </button>
                <button
                    type="button"
                    role="radio"
                    aria-checked={choice.mode === 'plan'}
                    disabled={plans.length === 0}
                    onClick={() => plans[0]?.id && onChange({ mode: 'plan', planId: String(plans[0].id) })}
                    className={`${tab(choice.mode === 'plan')} disabled:cursor-not-allowed disabled:opacity-50`}
                >
                    After your plan
                </button>
            </div>
            {plans.length === 0 && <p className="text-xs text-slate-500 mt-1">Save a plan in the module first to fill the form from the position after it.</p>}
            {choice.mode === 'plan' && plans.length > 1 && (
                <label className="mt-2 block text-xs text-slate-600">
                    Which plan
                    <select value={choice.planId} onChange={(e) => onChange({ mode: 'plan', planId: e.target.value })} className="mt-1 block w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
                        {plans.map((p) => <option key={String(p.id)} value={String(p.id)}>{planLabel(p)}</option>)}
                    </select>
                </label>
            )}
            {choice.mode === 'plan' && selectedPlan ? (
                <div className="mt-2 flex gap-2 rounded-lg border border-slate-200 border-l-4 border-l-amber-500 bg-white p-3 text-sm text-slate-800" role="note">
                    <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" />
                    <div>
                        <p><strong>Do not lodge this form until the changes in your plan have been made.</strong> It is filled in as things will stand after {plans.length > 1 ? 'the plan above' : `“${selectedPlan.name || 'your plan'}”`}, and Services Australia assesses what you own and earn on the day it decides.</p>
                        {resolved.status === 'loading' && <p className="mt-1 text-xs">Working out your position after the plan…</p>}
                        {resolved.status === 'failed' && <p className="mt-1 text-xs font-semibold">{resolved.reason}</p>}
                        {resolved.caveats.map((c) => <p key={c} className="mt-1 text-xs">{c}</p>)}
                    </div>
                </div>
            ) : (
                <div className="mt-2 flex gap-2 rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-600">
                    <Info size={14} className="mt-0.5 shrink-0 text-blue-600" />
                    <p>Filled in from what you own and earn today, to lodge now. If you lodge now and carry out your plan afterwards — for example, moving into care straight away and restructuring later — tell Services Australia about each change within 14 days.</p>
                </div>
            )}
        </div>
    );
}

/**
 * "Please review every question" — said up front, beside the buttons, not in the small print
 * (owner, 5 Oct 2026). The profile fills in what it holds; the rest of the form is the client's.
 */
export function ReviewEveryQuestion({ blanks }: { blanks: string }) {
    return (
        <div className="flex gap-2 rounded-lg border border-slate-200 border-l-4 border-l-blue-600 bg-white p-3 text-sm text-slate-800" role="note">
            <AlertTriangle size={16} className="mt-0.5 shrink-0 text-blue-600" />
            <div>
                <p className="font-semibold text-slate-900">Please review every question before you sign.</p>
                <p className="mt-1">We fill in the answers your profile holds, but we can’t complete every question for you. Check each answer we’ve written in, and answer the ones left blank — for example {blanks}.</p>
            </div>
        </div>
    );
}
